/**
 * Submittals — files released to the client portal, derived read-only from
 * `clientPortals/{portalId}/portalFileReleases`. Nothing here writes; the grouping is
 * pure so it can be unit-tested and reused by the Files tab and the tour.
 */

import type { PortalDesignPhase, PortalFileRelease, ProjectFile, ProjectFileStream } from '../types';
import {
  PROJECT_FILE_DETAIL_STAGE_LABELS,
  PROJECT_FILE_DETAIL_STAGES,
  type ProjectFileDetailStage,
} from './projectFileVersionGroups';
import { subscribePortalReleasesForDeal } from './portalFileReleaseService';
import {
  PORTAL_DESIGN_PHASE_LABELS,
  PORTAL_DESIGN_PHASES,
  portalReleaseDesignPhase,
  resolveReleaseStructureScopes,
  wholeProjectScope,
  type ReleaseStructureScope,
} from './portalReleasePhase';

export type SubmittalMediaKind = '2d' | '3d';

export const SUBMITTAL_MEDIA_LABELS: Record<SubmittalMediaKind, string> = {
  '2d': '2D Documentation',
  '3d': '3D Files',
};

export interface SubmittalEntry {
  release: PortalFileRelease;
  media: SubmittalMediaKind;
  /** LOD of the source hub version (absent when the version was never staged). */
  lod: ProjectFileDetailStage | null;
  lodLabel: string;
  streamTitle: string;
  structureNames: string[];
  sourceFile: ProjectFile | null;
  /** True when this is the newest release for its (stream, media) pair — what the client sees as current. */
  isCurrent: boolean;
  /** Concept / Engineering phase on the client approval trail. */
  phase: PortalDesignPhase;
  phaseLabel: string;
  /** Structure scope(s) — one per fork for whole-project releases later split into structure tracks. */
  scopes: ReleaseStructureScope[];
  /** Whole-project release later forked into structure tracks (rendered with a "Whole project" chip). */
  isWholeProjectRoot: boolean;
  /** Newer unapproved release of the same track + phase replaced this one. */
  superseded: boolean;
}

export type SubmittalGroupMode = 'lod' | 'phase';

export const SUBMITTAL_GROUP_MODE_LABELS: Record<SubmittalGroupMode, string> = {
  lod: 'LOD',
  phase: 'Phase',
};

/** Per-project persisted grouping preference. */
export function submittalGroupModeStorageKey(projectId: string): string {
  return `lctf.files.submittals.groupMode.${projectId}`;
}

export function readSubmittalGroupMode(projectId: string): SubmittalGroupMode {
  try {
    const v = window.localStorage.getItem(submittalGroupModeStorageKey(projectId));
    return v === 'phase' ? 'phase' : 'lod';
  } catch {
    return 'lod';
  }
}

export function writeSubmittalGroupMode(projectId: string, mode: SubmittalGroupMode): void {
  try {
    window.localStorage.setItem(submittalGroupModeStorageKey(projectId), mode);
  } catch {
    /* ignore */
  }
}

export interface SubmittalMediaGroup {
  media: SubmittalMediaKind;
  label: string;
  entries: SubmittalEntry[];
}

export interface SubmittalLodGroup {
  lod: ProjectFileDetailStage | null;
  label: string;
  media: SubmittalMediaGroup[];
  total: number;
}

/** Phase → structure scope → media. */
export interface SubmittalScopeGroup {
  key: string;
  label: string;
  media: SubmittalMediaGroup[];
  total: number;
}

export interface SubmittalPhaseGroup {
  phase: PortalDesignPhase;
  label: string;
  scopes: SubmittalScopeGroup[];
  total: number;
}

export const UNGROUPED_LOD_LABEL = 'No LOD set';

export function submittalMediaForStreamKind(kind: PortalFileRelease['streamKind']): SubmittalMediaKind {
  return kind === 'three_d_html' ? '3d' : '2d';
}

function releaseTime(r: PortalFileRelease): number {
  return r.releasedAt instanceof Date ? r.releasedAt.getTime() : 0;
}

/** Highest LOD first (what the client most recently progressed to), then "No LOD set". */
export function lodDisplayOrder(): (ProjectFileDetailStage | null)[] {
  const order: (ProjectFileDetailStage | null)[] = [...PROJECT_FILE_DETAIL_STAGES].reverse();
  order.push(null);
  return order;
}

export interface BuildSubmittalGroupsInput {
  releases: PortalFileRelease[];
  files: ProjectFile[];
  streams: ProjectFileStream[];
}

/** Flat, newest-first submittal entries with phase / scope / current flags resolved. */
export function buildSubmittalEntries(input: BuildSubmittalGroupsInput): SubmittalEntry[] {
  const fileById = new Map<string, ProjectFile>();
  for (const f of input.files) if (f.id) fileById.set(f.id, f);
  const streamById = new Map<string, ProjectFileStream>();
  for (const s of input.streams) if (s.id) streamById.set(s.id, s);
  const ctx = { files: input.files, streams: input.streams };

  const sorted = [...input.releases].sort((a, b) => releaseTime(b) - releaseTime(a));

  // Newest release per (sourceStreamId, media) = what the portal currently shows.
  const currentKeys = new Set<string>();
  return sorted.map(release => {
    const media = submittalMediaForStreamKind(release.streamKind);
    const sourceFile = fileById.get(release.sourceFileId) ?? null;
    const stream = streamById.get(release.sourceStreamId) ?? null;
    const lod = sourceFile?.detailStage ?? null;
    const currentKey = `${release.sourceStreamId}|${media}`;
    const isCurrent = !currentKeys.has(currentKey);
    if (isCurrent) currentKeys.add(currentKey);
    const phase = portalReleaseDesignPhase(release);
    const scopes = resolveReleaseStructureScopes(release, ctx);
    const isWholeProjectRoot = scopes.some(s => s.inferredFromFork) || !!stream?.wholeProjectRoot;
    const snapshotNames = (release.structureNames ?? []).filter(Boolean);
    return {
      release,
      media,
      lod,
      lodLabel: lod ? PROJECT_FILE_DETAIL_STAGE_LABELS[lod] : UNGROUPED_LOD_LABEL,
      streamTitle: stream?.title || release.sourceStreamTitle || release.displayLabel,
      structureNames: snapshotNames.length > 0 ? snapshotNames : (stream?.structureNames ?? []).filter(Boolean),
      sourceFile,
      isCurrent,
      phase,
      phaseLabel: PORTAL_DESIGN_PHASE_LABELS[phase],
      scopes,
      isWholeProjectRoot,
      superseded: !!release.supersededByReleaseId,
    };
  });
}

function splitByMedia(list: SubmittalEntry[]): SubmittalMediaGroup[] {
  const media: SubmittalMediaGroup[] = [];
  for (const m of ['2d', '3d'] as SubmittalMediaKind[]) {
    const mediaEntries = list.filter(e => e.media === m);
    if (mediaEntries.length === 0) continue;
    media.push({ media: m, label: SUBMITTAL_MEDIA_LABELS[m], entries: mediaEntries });
  }
  return media;
}

/**
 * Group portal releases by LOD → media (2D / 3D). Entries inside a media group are
 * newest first. Releases whose source version has no LOD land in "No LOD set".
 */
export function buildSubmittalGroups(input: BuildSubmittalGroupsInput): SubmittalLodGroup[] {
  const entries = buildSubmittalEntries(input);

  const byLod = new Map<ProjectFileDetailStage | null, SubmittalEntry[]>();
  for (const e of entries) {
    const list = byLod.get(e.lod) ?? [];
    list.push(e);
    byLod.set(e.lod, list);
  }

  const groups: SubmittalLodGroup[] = [];
  for (const lod of lodDisplayOrder()) {
    const list = byLod.get(lod);
    if (!list || list.length === 0) continue;
    groups.push({
      lod,
      label: lod ? PROJECT_FILE_DETAIL_STAGE_LABELS[lod] : UNGROUPED_LOD_LABEL,
      media: splitByMedia(list),
      total: list.length,
    });
  }
  return groups;
}

/**
 * Scope(s) a submittal is listed under. A release made before the project was split into
 * structures was submitted once, for the whole project — it is listed once under
 * "Whole project" rather than repeated under every structure track later forked from it.
 * (The approval trail still expands those forks itself; see {@link resolveReleaseStructureScopes}.)
 */
export function submittalListingScopes(e: Pick<SubmittalEntry, 'scopes' | 'isWholeProjectRoot'>): ReleaseStructureScope[] {
  if (e.isWholeProjectRoot) return [wholeProjectScope()];
  return e.scopes.length > 0 ? e.scopes : [wholeProjectScope()];
}

/**
 * Group portal releases by design phase → structure scope → media. Releases made before a
 * structure split land once under "Whole project"; releases from structure tracks land
 * under their own scope. Engineering is listed first (most recent work), then Concept.
 */
export function buildSubmittalPhaseGroups(input: BuildSubmittalGroupsInput): SubmittalPhaseGroup[] {
  const entries = buildSubmittalEntries(input);
  const byPhase = new Map<PortalDesignPhase, Map<string, { label: string; entries: SubmittalEntry[] }>>();
  for (const e of entries) {
    let scopes = byPhase.get(e.phase);
    if (!scopes) {
      scopes = new Map();
      byPhase.set(e.phase, scopes);
    }
    for (const s of submittalListingScopes(e)) {
      const bucket = scopes.get(s.key) ?? { label: s.label, entries: [] };
      bucket.entries.push(e);
      scopes.set(s.key, bucket);
    }
  }
  const phaseOrder: PortalDesignPhase[] = [...PORTAL_DESIGN_PHASES].reverse();
  const out: SubmittalPhaseGroup[] = [];
  for (const phase of phaseOrder) {
    const scopes = byPhase.get(phase);
    if (!scopes || scopes.size === 0) continue;
    const scopeGroups: SubmittalScopeGroup[] = [];
    // Whole project first, then alphabetical by label for stable lanes.
    const keys = Array.from(scopes.keys()).sort((a, b) => {
      if (a === '__whole_project__') return -1;
      if (b === '__whole_project__') return 1;
      return (scopes.get(a)?.label ?? '').localeCompare(scopes.get(b)?.label ?? '');
    });
    let total = 0;
    for (const key of keys) {
      const bucket = scopes.get(key)!;
      total += bucket.entries.length;
      scopeGroups.push({ key, label: bucket.label, media: splitByMedia(bucket.entries), total: bucket.entries.length });
    }
    out.push({ phase, label: PORTAL_DESIGN_PHASE_LABELS[phase], scopes: scopeGroups, total });
  }
  return out;
}

/** Count of distinct current submittals (one per stream + media). */
export function countCurrentSubmittals(groups: SubmittalLodGroup[]): number {
  let n = 0;
  for (const g of groups) for (const m of g.media) for (const e of m.entries) if (e.isCurrent) n += 1;
  return n;
}

/** Same as {@link countCurrentSubmittals} from a flat entry list (phase grouping). */
export function countCurrentSubmittalEntries(entries: SubmittalEntry[]): number {
  return entries.filter(e => e.isCurrent).length;
}

/** Live portal releases for this deal; resolves to [] when no portal is linked. */
export function subscribeProjectSubmittals(
  portalId: string | null,
  dealId: string | null | undefined,
  onData: (releases: PortalFileRelease[]) => void,
): () => void {
  if (!portalId || !dealId) {
    onData([]);
    return () => {};
  }
  return subscribePortalReleasesForDeal(portalId, dealId, onData);
}
